import { useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ALLOWED_PATHNAMES_DIRECT_MAIL_TERM, DTO_SESSION_TIMEOUT } from "constants/sessionTimeouts";
import { clearPersistedState } from "store/persistUtils";
import { ROUTES } from "routes/constants";
import { GA_EVENT_CONFIGS, GA_DEDUP_KEYS } from "constants/gaEvents";
import { pushGTMEventOnce } from "utils/gaEventDedup";
import { useAppSelector } from "store/hooks";
import { useFormState } from "hooks/useFormState";

interface InactivityHandlerProps {
    onTimeout?: () => void;
}

function isBrowserReloadShortcut(e: KeyboardEvent): boolean {
    if (!e.key) {
        return false;
    }
    if (e.key === "F5") {
        return true;
    }
    const k = e.key.toLowerCase();
    return k === "r" && (e.ctrlKey || e.metaKey);
}

const InactivityHandler = ({ onTimeout }: InactivityHandlerProps) => {
    const timeoutRef = useRef<number | null>(null);
    const isReloadIntentRef = useRef(false);
    const location = useLocation();
    const navigate = useNavigate();
    const saveContId = useAppSelector((state) => state.application.saveContId);
    const {
        state: {
            session: { timeout: sessionTimeout },
        },
    } = useFormState();

    const effectiveTimeout = typeof sessionTimeout === "number" ? sessionTimeout : DTO_SESSION_TIMEOUT;

    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        if (isBrowserReloadShortcut(e)) {
            isReloadIntentRef.current = true;
        }
    }, []);

    const handleTimeout = useCallback(async () => {
        if (saveContId) {
            pushGTMEventOnce(
                `${GA_DEDUP_KEYS.ABANDON_SESSION_TIMEOUT}:${saveContId}`,
                GA_EVENT_CONFIGS.SAVE_CONTINUE_ABANDON_SESSION_TIMEOUT
            );
        }
        await clearPersistedState();
        navigate(ROUTES.SESSION_EXPIRED);
    }, [navigate, saveContId]);

    const handleBeforeUnload = useCallback(() => {
        if (isReloadIntentRef.current) {
            isReloadIntentRef.current = false;
            return;
        }
        pushGTMEventOnce(GA_DEDUP_KEYS.ABANDON_TAB_CLOSE, GA_EVENT_CONFIGS.SAVE_CONTINUE_ABANDON_TAB_CLOSE);
    }, []);

    const resetTimer = useCallback(() => {
        if (timeoutRef.current) {
            globalThis.window.clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = globalThis.window.setTimeout(onTimeout || handleTimeout, effectiveTimeout);
    }, [onTimeout, handleTimeout, effectiveTimeout]);

    useEffect(() => {
        if (!ALLOWED_PATHNAMES_DIRECT_MAIL_TERM.includes(location.pathname)) {
            return;
        }

        const events = ["mousemove", "mousedown", "keypress", "scroll", "touchstart"];

        events.forEach((event) => {
            globalThis.window.addEventListener(event, resetTimer);
        });
        globalThis.window.addEventListener("keydown", handleKeyDown);
        globalThis.window.addEventListener("beforeunload", handleBeforeUnload);

        resetTimer();

        return () => {
            if (timeoutRef.current) {
                globalThis.window.clearTimeout(timeoutRef.current);
            }
            events.forEach((event) => {
                globalThis.window.removeEventListener(event, resetTimer);
            });
            globalThis.window.removeEventListener("keydown", handleKeyDown);
            globalThis.window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [location.pathname, resetTimer, handleKeyDown, handleBeforeUnload]);

    return null;
};

export default InactivityHandler;
